import { getDefaultReportParameters } from "./reportParameters";

const TDOE_DATA_DASHBOARD_ORGANIZATION_ID =
  "22a78f5f-a360-49c7-a89c-c939fc137c15";

const getQueryParam = (name) => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
};

// TDOE reports expect SchoolYear as a "YYYY-YYYY" string (matching the
// Title shown in the School Year dropdown), not the raw SchoolYearIdentifier
// (e.g. 2026) that other reports' SchoolYear parameter uses.
const getTDOESchoolYear = (reportParameters) => {
  const schoolYearIdentifier = parseInt(reportParameters.schoolYear, 10);
  return Number.isNaN(schoolYearIdentifier)
    ? null
    : `${schoolYearIdentifier - 1}-${schoolYearIdentifier}`;
};

const getTDOEReportDefinition = (reportTrdp, supportsGroupBy) => {
  const title = getQueryParam("title");
  return {
    getPermissions: () => ["TDOEDataDashboardReporting"],
    reportTitle: title || reportTrdp.replace(".trdp", ""),
    getReportTrdp: () => reportTrdp,
    getReportParameters: (reportParameters) => ({
      OrganizationId:
        reportParameters.organizationId || TDOE_DATA_DASHBOARD_ORGANIZATION_ID,
      // District is optional here: when no districts are selected, null
      // preserves today's all-districts behavior. Uppercased because the
      // report viewer's AvailableValues match against District appears to
      // be case-sensitive against uppercase GUIDs, while the org tree here
      // returns lowercase GUIDs (same reasoning as Teachers-by-Period below).
      District: reportParameters.districtIds
        ? reportParameters.districtIds.split(",").map((id) => id.toUpperCase())
        : null,
      SchoolId: reportParameters.schoolId || null,
      ClusterId: reportParameters.clusterId || null,
      DataCollectionPeriod: reportParameters.dataCollectionPeriod,
      SchoolYear: getTDOESchoolYear(reportParameters),
      QuestionSurveyTemplateFieldId:
        reportParameters.questionSurveyTemplateFieldId ||
        getQueryParam("qstf"),
      GroupBy: reportParameters.groupBy || getQueryParam("groupBy"),
      Title: title,
    }),
    getReportParametersDisplay: (reportParametersDisplay) => {
      reportParametersDisplay.isOrganizationDisplayed = false;
      reportParametersDisplay.isDistrictDisplayed = false;
      reportParametersDisplay.isDistrictMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolDisplayed = false;
      reportParametersDisplay.isClusterDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodDisplayed = true;
      reportParametersDisplay.isSchoolYearDisplayed = true;
      reportParametersDisplay.isGroupByDisplayed = supportsGroupBy === true;
      return reportParametersDisplay;
    },
    getRequiredReportParameters: (requiredReportParameters) => {
      requiredReportParameters.isDataCollectionPeriodRequired = true;
      requiredReportParameters.isGroupByRequired = supportsGroupBy === true;
      return requiredReportParameters;
    },
  };
};

const getTDOEPeriodBreakdownReportDefinition = (reportTrdp) => {
  const title = getQueryParam("title");
  return {
    getPermissions: () => ["TDOEDataDashboardReporting"],
    reportTitle: title || reportTrdp.replace(".trdp", ""),
    getReportTrdp: () => reportTrdp,
    getReportParameters: (reportParameters) => ({
      OrganizationId:
        reportParameters.organizationId || TDOE_DATA_DASHBOARD_ORGANIZATION_ID,
      // District is optional here: when no districts are selected, null
      // preserves today's all-districts behavior. Uppercased because the
      // report viewer's AvailableValues match against District appears to
      // be case-sensitive against uppercase GUIDs, while the org tree here
      // returns lowercase GUIDs (same reasoning as Teachers-by-Period below).
      District: reportParameters.districtIds
        ? reportParameters.districtIds.split(",").map((id) => id.toUpperCase())
        : null,
      // This report compares BOY and MOY side by side, so DataCollectionPeriod
      // is a multi-value Telerik parameter here (unlike the single-period
      // reports above) and must be passed as an array, not a joined string.
      DataCollectionPeriod: reportParameters.dataCollectionPeriod
        ? reportParameters.dataCollectionPeriod.split(",")
        : null,
      SchoolYear: getTDOESchoolYear(reportParameters),
      QuestionSurveyTemplateFieldId:
        reportParameters.questionSurveyTemplateFieldId ||
        getQueryParam("qstf"),
      GroupBy: reportParameters.groupBy || getQueryParam("groupBy"),
      Title: title,
    }),
    getReportParametersDisplay: (reportParametersDisplay) => {
      reportParametersDisplay.isOrganizationDisplayed = false;
      reportParametersDisplay.isDistrictDisplayed = false;
      reportParametersDisplay.isDistrictMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolDisplayed = false;
      reportParametersDisplay.isClusterDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolYearDisplayed = true;
      reportParametersDisplay.isGroupByDisplayed = false;
      return reportParametersDisplay;
    },
    getRequiredReportParameters: (requiredReportParameters) => {
      requiredReportParameters.isDataCollectionPeriodRequired = true;
      return requiredReportParameters;
    },
  };
};

const getTDOEClusterBarChartReportDefinition = (reportTrdp) => {
  const title = getQueryParam("title");
  return {
    getPermissions: () => ["TDOEDataDashboardReporting"],
    reportTitle: title || reportTrdp.replace(".trdp", ""),
    getReportTrdp: () => reportTrdp,
    getReportParameters: (reportParameters) => ({
      OrganizationId:
        reportParameters.organizationId || TDOE_DATA_DASHBOARD_ORGANIZATION_ID,
      // District is optional here: when no districts are selected, null
      // preserves today's all-districts behavior. Uppercased because the
      // report viewer's AvailableValues match against District appears to
      // be case-sensitive against uppercase GUIDs, while the org tree here
      // returns lowercase GUIDs (same reasoning as Teachers-by-Period below).
      District: reportParameters.districtIds
        ? reportParameters.districtIds.split(",").map((id) => id.toUpperCase())
        : null,
      DataCollectionPeriod: reportParameters.dataCollectionPeriod,
      SchoolYear: getTDOESchoolYear(reportParameters),
      GradeLevel: reportParameters.gradeLevel || getQueryParam("gradeLevel"),
      CoreAction: reportParameters.coreAction || getQueryParam("coreAction"),
      Title: title,
    }),
    getReportParametersDisplay: (reportParametersDisplay) => {
      reportParametersDisplay.isOrganizationDisplayed = false;
      reportParametersDisplay.isDistrictDisplayed = false;
      reportParametersDisplay.isDistrictMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolDisplayed = false;
      reportParametersDisplay.isClusterDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodDisplayed = true;
      reportParametersDisplay.isSchoolYearDisplayed = true;
      reportParametersDisplay.isGroupByDisplayed = false;
      reportParametersDisplay.isGradeLevelDisplayed = true;
      return reportParametersDisplay;
    },
    getRequiredReportParameters: (requiredReportParameters) => {
      requiredReportParameters.isDataCollectionPeriodRequired = true;
      requiredReportParameters.isGradeLevelRequired = true;
      return requiredReportParameters;
    },
  };
};

const getTDOEHorizontalBarChartReportDefinition = (reportTrdp) => {
  const title = getQueryParam("title");
  return {
    getPermissions: () => ["TDOEDataDashboardReporting"],
    reportTitle: title || reportTrdp.replace(".trdp", ""),
    getReportTrdp: () => reportTrdp,
    getReportParameters: (reportParameters) => ({
      OrganizationId:
        reportParameters.organizationId || TDOE_DATA_DASHBOARD_ORGANIZATION_ID,
      // District is optional here: when no districts are selected, null
      // preserves today's all-districts behavior. Uppercased because the
      // report viewer's AvailableValues match against District appears to
      // be case-sensitive against uppercase GUIDs, while the org tree here
      // returns lowercase GUIDs (same reasoning as Teachers-by-Period below).
      District: reportParameters.districtIds
        ? reportParameters.districtIds.split(",").map((id) => id.toUpperCase())
        : null,
      // This report compares BOY and EOY per grade level, so DataCollectionPeriod
      // is a multi-value Telerik parameter here and must be passed as an array,
      // not a joined string. There is no GroupBy parameter for this report - it
      // is always grouped by grade level.
      DataCollectionPeriod: reportParameters.dataCollectionPeriod
        ? reportParameters.dataCollectionPeriod.split(",")
        : null,
      SchoolYear: getTDOESchoolYear(reportParameters),
      QuestionSurveyTemplateFieldId:
        reportParameters.questionSurveyTemplateFieldId ||
        getQueryParam("qstf"),
      Title: title,
    }),
    getReportParametersDisplay: (reportParametersDisplay) => {
      reportParametersDisplay.isOrganizationDisplayed = false;
      reportParametersDisplay.isDistrictDisplayed = false;
      reportParametersDisplay.isDistrictMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolDisplayed = false;
      reportParametersDisplay.isClusterDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolYearDisplayed = true;
      reportParametersDisplay.isGroupByDisplayed = false;
      return reportParametersDisplay;
    },
    getRequiredReportParameters: (requiredReportParameters) => {
      requiredReportParameters.isDataCollectionPeriodRequired = true;
      return requiredReportParameters;
    },
  };
};

const getTDOETeachersByPeriodReportDefinition = (reportTrdp) => {
  const title = getQueryParam("title");
  return {
    getPermissions: () => ["TDOEDataDashboardReporting"],
    reportTitle: title || reportTrdp.replace(".trdp", ""),
    getReportTrdp: () => reportTrdp,
    getReportParameters: (reportParameters) => ({
      OrganizationId:
        reportParameters.organizationId || TDOE_DATA_DASHBOARD_ORGANIZATION_ID,
      // This report compares BOY/MOY/EOY side by side, so DataCollectionPeriod
      // is a multi-value Telerik parameter here and must be passed as an array,
      // not a joined string.
      DataCollectionPeriod: reportParameters.dataCollectionPeriod
        ? reportParameters.dataCollectionPeriod.split(",")
        : null,
      SchoolYear: getTDOESchoolYear(reportParameters),
      QuestionSurveyTemplateFieldId:
        reportParameters.questionSurveyTemplateFieldId ||
        getQueryParam("qstf"),
      // Unlike every other TDOE report, this one is school-scoped: the user
      // must pick one or more districts and one or more schools in the
      // report viewer. SchoolIds is a cascading multi-value Telerik
      // parameter (its valid values depend on District), so District must
      // also be passed - not just SchoolIds - or every SchoolIds value will
      // appear invalid. Both are uppercased because the report's cascading
      // valid-values comparison for SchoolIds appears to be case-sensitive
      // against uppercase GUIDs, while the org tree here returns lowercase
      // GUIDs.
      District: reportParameters.districtIds
        ? reportParameters.districtIds.split(",").map((id) => id.toUpperCase())
        : null,
      SchoolIds: reportParameters.schoolIds
        ? reportParameters.schoolIds.split(",").map((id) => id.toUpperCase())
        : null,
      Title: title,
    }),
    getReportParametersDisplay: (reportParametersDisplay) => {
      reportParametersDisplay.isOrganizationDisplayed = false;
      reportParametersDisplay.isDistrictDisplayed = false;
      reportParametersDisplay.isDistrictMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolDisplayed = false;
      reportParametersDisplay.isSchoolMultiSelectDisplayed = true;
      reportParametersDisplay.isClusterDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodDisplayed = false;
      reportParametersDisplay.isDataCollectionPeriodMultiSelectDisplayed = true;
      reportParametersDisplay.isSchoolYearDisplayed = true;
      reportParametersDisplay.isGroupByDisplayed = false;
      return reportParametersDisplay;
    },
    getRequiredReportParameters: (requiredReportParameters) => {
      requiredReportParameters.isDataCollectionPeriodRequired = true;
      requiredReportParameters.isDistrictIdsRequired = true;
      requiredReportParameters.isSchoolIdsRequired = true;
      return requiredReportParameters;
    },
  };
};

export const getReportByUrl = (reportUrl) => {
  switch (reportUrl) {
    case "school-administrator-evaluator-certification":
      return {
        getPermissions: () => [
          "ReportsSchoolAdministratorEvaluatorCertificationStatus",
          "ReportsSchoolAdministratorEvaluatorCertificationResets",
          "ReportsSchoolAdministratorEvaluatorCertificationFailures",
          "ReportsSchoolAdministratorEvaluatorCertificationResults",
        ],
        reportTitle: "School Administrator Evaluator Certification",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "status";
            case "1":
              return "resets";
            case "2":
              return "failures";
            case "3":
              return "results";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) => {
          switch (reportType) {
            case "status":
              return "SchoolAdministratorEvaluatorCertificationStatus.trdp";
            case "resets":
              return "SchoolAdministratorEvaluatorCertificationResets.trdp";
            case "failures":
              return "SchoolAdministratorEvaluatorCertificationFailures.trdp";
            case "results":
              return "SchoolAdministratorEvaluatorCertificationResults.trdp";
            default:
              return "";
          }
        },
        getReportParameters: (reportParameters, reportType) => {
          switch (reportType) {
            case "status":
              return {
                DistrictId: reportParameters.districtId,
                Inactive: reportParameters.includeInactiveUsers,
                isd: reportParameters.includeDirectors,
                isn: reportParameters.includeNietAdministrators,
                OrganizationId: reportParameters.organizationId,
                SchoolId: reportParameters.schoolId,
                SchoolYear: reportParameters.schoolYear,
              };
            case "resets":
            case "failures":
            case "results":
              return {
                DistrictId: reportParameters.districtId,
                OrganizationId: reportParameters.organizationId,
                SchoolId: reportParameters.schoolId,
                SchoolYear: reportParameters.schoolYear,
              };
            default:
              return {};
          }
        },
        getReportParametersDisplay: (reportParametersDisplay, reportType) => {
          reportParametersDisplay.isEvaluatorCertificationTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;

          if (reportType === "status") {
            reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
            reportParametersDisplay.isIncludeNietAdministratorsDisplayed = true;
            reportParametersDisplay.isIncludeDirectorsDisplayed = true;
          }
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters, reportType) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          if (reportType === "status") {
            requiredReportParameters.isIncludeInactiveUsersRequired = true;
          }
          return requiredReportParameters;
        },
      };
    case "school-administrator-evaluator-certification-status":
      return {
        getPermissions: () => [
          "ReportsSchoolAdministratorEvaluatorCertificationStatus",
        ],
        reportTitle: "School Admin Evaluator Certification Status",
        getReportTrdp: () => {
          return "SchoolAdministratorEvaluatorCertificationStatus.trdp";
        },
        getReportParameters: (reportParameters) => {
          return {
            DistrictId: reportParameters.districtId,
            Inactive: reportParameters.includeInactiveUsers,
            isd: reportParameters.includeDirectors,
            isn: reportParameters.includeNietAdministrators,
            OrganizationId: reportParameters.organizationId,
            SchoolId: reportParameters.schoolId,
            SchoolYear: reportParameters.schoolYear,
          };
        },
        getReportParametersDisplay: (reportParametersDisplay, reportType) => {
          reportParametersDisplay.isEvaluatorCertificationTypeDisplayed = false;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          reportParametersDisplay.isIncludeNietAdministratorsDisplayed = true;
          reportParametersDisplay.isIncludeDirectorsDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "user-export":
      return {
        getPermissions: () => ["ReportsUserExport"],
        reportTitle: "User Export",
        getReportTrdp: () => "UserExport.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          Inactive: reportParameters.includeInactiveUsers,
          isd: reportParameters.includeDirectors,
          isn: reportParameters.includeNietAdministrators,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          reportParametersDisplay.isIncludeNietAdministratorsDisplayed = true;
          reportParametersDisplay.isIncludeDirectorsDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };

    case "average-observer-vs-self-score":
      return {
        getPermissions: () => ["ReportsAverageObserverVsSelfScore"],
        reportTitle: "Average Observer vs Self Score",
        getReportTrdp: () => "AverageObserverVsSelfScore.trdp",
        getIndividualDefaultReportParameters: {
          ...getDefaultReportParameters(),
          isSummarizeAtLevel: true,
        },
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          Uids: reportParameters.teacherUserIds,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtOrganizationDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          return requiredReportParameters;
        },
      };
    case "compass-equivalent":
      return {
        getPermissions: () => ["ReportsCompassEquivalent"],
        reportTitle: "Compass Equivalent",
        getReportTrdp: () => "CompassEquivalent.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          Inactive: reportParameters.includeInactiveUsers,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "educator-average-by-rubric-domain":
      return {
        getPermissions: () => ["ReportsEducatorAverageByRubricDomain"],
        reportTitle: "Educator Average by Rubric Domain",
        getReportTrdp: () => "EducatorAverageByRubricDomain.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersDisplayed = true;
          return requiredReportParameters;
        },
      };
    case "historical-averages-by-educator":
      return {
        getPermissions: () => ["ReportsHistoricalAveragesByEducator"],
        reportTitle: "Historical Averages by Educator",
        getReportTrdp: () => "HistoricalAveragesByEducator.trdp",
        getIndividualDefaultReportParameters: {
          ...getDefaultReportParameters(),
          isSummarizeAtLevel: false,
        },
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          UserIds: reportParameters.teacherUserIds,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtOrganizationDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          return requiredReportParameters;
        },
      };
    case "observation-refinement-or-reinforcement-descriptors":
      return {
        getPermissions: () => [
          "ReportsObservationRefinementReinforcementDescriptors",
        ],
        reportTitle: "Observation Refinement & Reinforcement Descriptors",
        getReportTrdp: () =>
          "ObservationRefinementReinforcementDescriptors.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          UserIds: reportParameters.teacherUserIds,
          SchoolYear: reportParameters.schoolYear,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersDisplayed = true;
          return requiredReportParameters;
        },
      };
    case "observation-status":
      return {
        getPermissions: () => ["ReportsTeacherObservationStatus"],
        reportTitle: "Teacher Observation Status",
        getReportTrdp: () => "TeacherObservationStatus.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          return requiredReportParameters;
        },
      };
    case "observation-summary-by-educator":
      return {
        getPermissions: () => ["ReportsObservationSummaryByEducator"],
        reportTitle: "Observation Summary by Educator",
        getReportTrdp: () => "ObservationSummaryByEducator.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          return requiredReportParameters;
        },
      };
    case "observer-average-by-rubric-domain":
      return {
        getPermissions: () => ["ReportsObserverAverageByRubricDomain"],
        reportTitle: "Observer Average by Rubric Domain",
        getReportTrdp: () => "ObserverAverageByRubricDomain.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          UIds: reportParameters.teacherUserIds,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          return requiredReportParameters;
        },
      };
    case "overall-average-by-school-administrator":
      return {
        getPermissions: () => ["ReportsOverallAverageBySchoolAdministrator"],
        reportTitle: "Overall Average by School Administrator",
        getReportTrdp: () => "OverallAverageBySchoolAdministrator.trdp",
        getIndividualDefaultReportParameters: {
          ...getDefaultReportParameters(),
          isSummarizeAtLevel: true,
        },
        getReportParameters: (reportParameters) => ({
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtOrganizationDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isAdminDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-payout":
      return {
        getPermissions: () => [""],
        reportTitle: "School Administrator Payout",
        getReportTrdp: () => "SchoolAdministratorPayout.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictID: reportParameters.districtId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isDistrictIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-average-by-rubric-domain":
      return {
        getPermissions: () => [
          "ReportsSchoolAdministratorAverageByRubricDomain",
        ],
        reportTitle: "School Administrator Average by Rubric Domain",
        getReportTrdp: () => "SchoolAdministratorAverageByRubricDomain.trdp",
        getIndividualDefaultReportParameters: {
          ...getDefaultReportParameters(),
          isSummarizeAtLevel: true,
        },
        getReportParameters: (reportParameters) => ({
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtOrganizationDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isAdminDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersDisplayed = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-end-of-year-rating":
      return {
        getPermissions: () => [
          "ReportsSchoolAdministratorEndOfYearRatingRounding",
          "ReportsSchoolAdministratorEndOfYearRatingNoRounding",
        ],
        reportTitle: "School Administrator End of Year Rating",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "rounding";
            case "1":
              return "no-rounding";
            default:
              return "";
          }
        },
        getReportTrdp: () => "SchoolAdministratorEndOfYearRating.trdp",
        getReportParameters: (reportParameters, reportType) => ({
          DistrictId: reportParameters.districtId,
          IsRounded: reportType === "rounding",
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          IsNIETAdmin: reportParameters.isNIETAdmin,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isRoundingTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-individual-summary":
      return {
        getPermissions: () => ["ReportsSchoolAdministratorIndividualSummary"],
        reportTitle: "School Administrator Individual Summary",
        getReportTrdp: () => "SchoolAdministratorIndividualSummary.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isAdminDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "leadership-log-categories":
      return {
        getPermissions: () => ["ReportsLeadershipLogCategories"],
        reportTitle: "Leadership Log Categories",
        getReportTrdp: () => "LeadershipLogCategories.trdp",
        getReportParameters: (reportParameters) => ({
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-observation-summary":
      return {
        getPermissions: () => ["ReportsSchoolAdministratorObservationSummary"],
        reportTitle: "School Administrator Observation Summary",
        getReportTrdp: () => "SchoolAdministratorObservationSummary.trdp",
        getIndividualDefaultReportParameters: {
          ...getDefaultReportParameters(),
          isSummarizeAtLevel: true,
        },
        getReportParameters: (reportParameters) => ({
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtOrganizationDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isAdminDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-refinement-and-reinforcement-goals":
      return {
        getPermissions: () => [
          "ReportsSchoolAdministratorRefinementAndReinforcementGoalsRefinement",
          "ReportsSchoolAdministratorRefinementAndReinforcementGoalsReinforcement",
        ],
        reportTitle: "School Administrator Refinement and Reinforcement Goals",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "refinement";
            case "1":
              return "reinforcement";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) =>
          reportType === "refinement"
            ? "SchoolAdministratorRefinementGoals.trdp"
            : "SchoolAdministratorReinforcementGoals.trdp",
        getIndividualDefaultReportParameters: {
          ...getDefaultReportParameters(),
          isSummarizeAtLevel: true,
        },
        getReportParameters: (reportParameters) => ({
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isRefinementOrReinforcementTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtOrganizationDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isAdminDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-administrator-survey-report":
      return {
        getPermissions: () => ["ReportsSchoolAdministratorSurveyReport"],
        reportTitle: "School Administrator Survey Report",
        getReportTrdp: () => "SchoolAdministratorSurveyReport.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-evaluator-certification":
      return {
        getPermissions: () => [
          "ReportsTeacherEvaluatorCertificationStatus",
          "ReportsTeacherEvaluatorCertificationResets",
          "ReportsTeacherEvaluatorCertificationFailures",
          "ReportsTeacherEvaluatorCertificationResults",
        ],
        reportTitle: "Teacher Evaluator Certification",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "status";
            case "1":
              return "resets";
            case "2":
              return "failures";
            case "3":
              return "results";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) => {
          switch (reportType) {
            case "status":
              return "TeacherEvaluatorCertificationStatus_NIET.trdp";
            case "resets":
              return "TeacherEvaluatorCertificationResets_NIET.trdp";
            case "failures":
              return "TeacherEvaluatorCertificationFailures_NIET.trdp";
            case "results":
              return "TeacherEvaluatorCertificationResults_NIET.trdp";
            default:
              return "";
          }
        },
        getReportParameters: (reportParameters, reportType) => {
          switch (reportType) {
            case "status":
              return {
                DistrictId: reportParameters.districtId,
                Inactive: reportParameters.includeInactiveUsers,
                isd: reportParameters.includeDirectors,
                isn: reportParameters.includeNietAdministrators,
                OrganizationId: reportParameters.organizationId,
                SchoolId: reportParameters.schoolId,
                SchoolYear: reportParameters.schoolYear,
              };
            case "resets":
            case "failures":
            case "results":
              return {
                DistrictId: reportParameters.districtId,
                OrganizationId: reportParameters.organizationId,
                SchoolId: reportParameters.schoolId,
                SchoolYear: reportParameters.schoolYear,
              };
            default:
              return {};
          }
        },
        getReportParametersDisplay: (reportParametersDisplay, reportType) => {
          reportParametersDisplay.isEvaluatorCertificationTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;

          if (reportType === "status") {
            reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
            reportParametersDisplay.isIncludeNietAdministratorsDisplayed = true;
            reportParametersDisplay.isIncludeDirectorsDisplayed = true;
          }
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters, reportType) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;

          if (reportType === "status") {
            requiredReportParameters.isIncludeInactiveUsersRequired = true;
          }
          return requiredReportParameters;
        },
      };
    case "teacher-evaluator-certification-status":
      return {
        getPermissions: () => ["ReportsTeacherEvaluatorCertificationStatus"],
        reportTitle: "Teacher Evaluator Certification Status",
        getReportTrdp: () => {
          return "TeacherEvaluatorCertificationStatus_NIET.trdp";
        },
        getReportParameters: (reportParameters) => {
          return {
            DistrictId: reportParameters.districtId,
            Inactive: reportParameters.includeInactiveUsers,
            isd: reportParameters.includeDirectors,
            isn: reportParameters.includeNietAdministrators,
            OrganizationId: reportParameters.organizationId,
            SchoolId: reportParameters.schoolId,
            SchoolYear: reportParameters.schoolYear,
          };
        },
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isEvaluatorCertificationTypeDisplayed = false;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          reportParametersDisplay.isIncludeNietAdministratorsDisplayed = true;
          reportParametersDisplay.isIncludeDirectorsDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "evaluator-certification-records-export":
      return {
        getPermissions: () => [
          "ReportsTeacherEvaluatorCertificationRecordsExport",
          "ReportsSchoolAdministratorEvaluatorCertificationRecordsExport",
        ],
        reportTitle: "Evaluator Certification Records Export",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "teacher";
            case "1":
              return "school-administrator";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) =>
          reportType === "teacher"
            ? "TeacherEvaluatorCertificationRecordsExport.trdp"
            : "SchoolAdministratorEvaluatorCertificationRecordsExport.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          Inactive: reportParameters.includeInactiveUsers,
          isd: reportParameters.includeDirectors,
          isn: reportParameters.includeNietAdministrators,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isTeacherOrSchoolAdministratorTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          reportParametersDisplay.isIncludeNietAdministratorsDisplayed = true;
          reportParametersDisplay.isIncludeDirectorsDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "evaluator-certification-statistics":
      return {
        getPermissions: () => [
          "ReportsTeacherEvaluatorCertificationStatistics",
          "ReportsSchoolAdministratorEvaluatorCertificationStatistics",
        ],
        reportTitle: "Evaluator Certification Statistics",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "teacher";
            case "1":
              return "school-administrator";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) =>
          reportType === "teacher"
            ? "TeacherEvaluatorCertificationStatistics.trdp"
            : "SchoolAdministratorEvaluatorCertificationStatistics.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictID: reportParameters.districtId,
          OrganizationId: reportParameters.organizationId,
          SchoolID: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isTeacherOrSchoolAdministratorTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-grade-level-average-by-rubric-domain":
      return {
        getPermissions: () => [
          "ReportsTeacherGradeLevelAveragesByRubricDomain",
        ],
        reportTitle: "Teacher Grade Level Averages by Rubric Domain",
        getReportTrdp: () => "TeacherGradeLevelAverageByRubricDomain.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterID: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictID: reportParameters.districtId,
          SchoolYear: reportParameters.schoolYear,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolID: reportParameters.schoolId,
          GradeLevels: reportParameters.gradeLevels,
          UIds: reportParameters.teacherUserIds,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isIncludeGradeLevels = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-individual-summary":
      return {
        getPermissions: () => ["ReportsTeacherIndividualSummary"],
        reportTitle: "Teacher Individual Summary",
        getReportTrdp: () => "TeacherIndividualSummary.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DistrictId: reportParameters.districtId,
          Inactive: reportParameters.includeInactiveUsers,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          WithOverallAvg: false,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-individual-summary-with-overall-avg":
      return {
        getPermissions: () => ["ReportsTeacherIndividualSummaryWithOverallAvg"],
        reportTitle: "Teacher Individual Summary with Overall Average",
        getReportTrdp: () => "TeacherIndividualSummary.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DistrictId: reportParameters.districtId,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
          WithOverallAvg: true,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };

    case "teacher-overall-average":
      return {
        getPermissions: () => [
          "ReportsTeacherOverallAverageRubricIndicator",
          "ReportsTeacherOverallAverageObserver",
          "ReportsTeacherOverallAverageEducator",
          "ReportsTeacherOverallAverageSubject",
        ],
        reportTitle: "Teacher Overall Average",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "rubric-indicator";
            case "1":
              return "observer";
            case "2":
              return "educator";
            case "3":
              return "subject";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) => {
          switch (reportType) {
            case "rubric-indicator":
              return "TeacherOverallAverageByRubricIndicator.trdp";
            case "observer":
              return "TeacherOverallAverageByObserver.trdp";
            case "educator":
              return "TeacherOverallAverageByEducator.trdp";
            case "subject":
              return "TeacherOverallAverageBySubject.trdp";
            default:
              return "";
          }
        },
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          SchoolYear: reportParameters.schoolYear,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          Uids: reportParameters.teacherUserIds,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isTeacherOverallAverageTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-payout":
      return {
        getPermissions: () => ["ReportsTeacherPayout"],
        reportTitle: "Teacher Payout",
        getReportTrdp: () => "TeacherPayout.trdp",
        getReportParameters: (reportParameters) => ({
          SchoolID: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isSchoolIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-payout-users":
      return {
        getPermissions: () => ["ReportsTeacherPayoutUsers"],
        reportTitle: "Teacher Payout Users",
        getReportTrdp: () => "TeacherPayoutUsers.trdp",
        getReportParameters: (reportParameters) => ({
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          requiredReportParameters.isSchoolIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-refinement-or-reinforcement-goals":
      return {
        getPermissions: () => [
          "ReportsTeacherRefinementAndReinforcementGoalsRefinement",
          "ReportsTeacherRefinementAndReinforcementGoalsReinforcement",
        ],
        reportTitle: "Teacher Refinement and Reinforcement Goals",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "refinement";
            case "1":
              return "reinforcement";
            default:
              return "";
          }
        },
        getReportTrdp: (reportType) =>
          reportType === "refinement"
            ? "TeacherRefinementGoals.trdp"
            : "TeacherReinforcementGoals.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DateRangeEnd: reportParameters.endDate,
          DateRangeStart: reportParameters.startDate,
          DistrictId: reportParameters.districtId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          Uids: reportParameters.teacherUserIds,
          SchoolYear: reportParameters.schoolYear,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isRefinementOrReinforcementTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isSelectDateRangeDisplayed = true;
          reportParametersDisplay.isSummarizeAtLevelDisplayed = true;
          reportParametersDisplay.isStartDateDisplayed = true;
          reportParametersDisplay.isEndDateDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isStartDateRequired = true;
          //requiredReportParameters.isEndDateRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-skr-summary":
      return {
        getPermissions: () => [
          "ReportsTeacherSKRSummaryRounding",
          "ReportsTeacherSKRSummaryNoRounding",
        ],
        reportTitle: "Teacher SKR Summary",
        getReportType: (reportTypeIndex) => {
          switch (reportTypeIndex) {
            case "0":
              return "rounding";
            case "1":
              return "no-rounding";
            default:
              return "";
          }
        },
        getReportTrdp: () => "TeacherSKRSummary.trdp",
        getReportParameters: (reportParameters, reportType) => ({
          ClusterId: reportParameters.clusterId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          IsRounded: reportType === "rounding",
          OrganizationId: reportParameters.organizationId,
          DistrictId: reportParameters.districtId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isRoundingTypeDisplayed = true;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-skr-summary-rounding":
      return {
        getPermissions: () => ["ReportsTeacherSKRSummaryRounding"],
        reportTitle: "Teacher SKR Summary",
        getReportTrdp: () => "TeacherSKRSummary.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          Individually: reportParameters.isSummarizeAtLevel === false,
          IsRounded: true,
          OrganizationId: reportParameters.organizationId,
          DistrictId: reportParameters.districtId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Inactive: reportParameters.includeInactiveUsers,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isRoundingTypeDisplayed = false;
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isReportTypeRequired = true;
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isSummarizeAtLevelRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "teacher-survey-report":
      return {
        getPermissions: () => ["ReportsTeacherSurveyReport"],
        reportTitle: "Teacher Survey Report",
        getReportTrdp: () => "TeacherSurveyReport.trdp",
        getReportParameters: (reportParameters) => ({
          ClusterId: reportParameters.clusterId,
          DistrictId: reportParameters.districtId,
          Inactive: reportParameters.includeInactiveUsers,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
          Uids: reportParameters.teacherUserIds,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          reportParametersDisplay.isClusterDisplayed = true;
          reportParametersDisplay.isSchoolYearDisplayed = true;
          reportParametersDisplay.isIncludeInactiveUsersDisplayed = true;
          reportParametersDisplay.isTeachersDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          //requiredReportParameters.isSchoolYearRequired = true;
          requiredReportParameters.isIncludeInactiveUsersRequired = true;
          return requiredReportParameters;
        },
      };
    case "school-authorization":
      return {
        getPermissions: () => ["ReportsSchoolAuthorization"],
        reportTitle: "School Authorization",
        getReportTrdp: () => "SchoolAuthorization.trdp",
        getReportParameters: (reportParameters) => ({
          DistrictId: reportParameters.districtId,
          OrganizationId: reportParameters.organizationId,
          SchoolId: reportParameters.schoolId,
          SchoolYear: reportParameters.schoolYear,
        }),
        getReportParametersDisplay: (reportParametersDisplay) => {
          reportParametersDisplay.isOrganizationDisplayed = true;
          reportParametersDisplay.isDistrictDisplayed = true;
          reportParametersDisplay.isSchoolDisplayed = true;
          return reportParametersDisplay;
        },
        getRequiredReportParameters: (requiredReportParameters) => {
          requiredReportParameters.isOrganizationIdRequired = true;
          return requiredReportParameters;
        },
      };
    case "tdoe-pie-chart":
      return getTDOEReportDefinition("TDOEPieChart.trdp", false);
    case "tdoe-bar-chart":
      return getTDOEReportDefinition("TDOEBarChart.trdp", true);
    case "tdoe-stacked-bar-chart":
      return getTDOEReportDefinition("TDOEStackedBarChart.trdp", true);
    case "tdoe-cluster-bar-chart":
      return getTDOEClusterBarChartReportDefinition("TDOEClusterBarChart.trdp");
    case "tdoe-period-breakdown-chart":
      return getTDOEPeriodBreakdownReportDefinition(
        "TDOEDataCollectionPeriodBreakdown.trdp"
      );
    case "tdoe-horizontal-bar-chart":
      return getTDOEHorizontalBarChartReportDefinition(
        "TDOEHorizontalBarChart.trdp"
      );
    case "tdoe-teachers-by-period-chart":
      return getTDOETeachersByPeriodReportDefinition(
        "TDOETeachersByPeriod.trdp"
      );

    default:
      throw new Error(`Unknown report url: ${reportUrl}`);
  }
};
